import React, { Fragment, } from 'react';
import { sortBy, omitBy, isUndefined, pick, keyBy, } from 'lodash';
import { toast } from 'react-toastify';
import { format as formatDate, } from 'date-fns';

import firebase from '../../firebase';
import { canUpdateSimulation, canApplySimulation, } from '../../shared/abilities';
import { fields, removeChangeFromList, } from '../../shared/models/simulation';
import { applySimulationToMaster, discardSimulation, } from '../../simulation';
import AdminPage from '../hocs/AdminPage';
import ModelFormModal from '../modals/ModelFormModal';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useFirebaseUser from '../hooks/useFirebaseUser';
import ModalButton from '../ModalButton';
import ProgressButton from '../ProgressButton';
import EditButton from '../EditButton';
import TenantLink from '../TenantLink';
import InventoriesHeader from '../InventoriesHeader';
import SimulationChangesTable from '../SimulationChangesTable';

const db = firebase.firestore();
const simulationsRef = db.collection('simulations');

export default AdminPage(function AdminSimulations (props) {
  const { tenant, user } = props;
  const { firebaseUser } = useFirebaseUser();
  const simulations = sortBy(
    useCollectionSubscriptionInTenant(simulationsRef),
    _ => -(_.createdAt?.toDate?.().getTime() ?? 0)
  );
  const activeSimulations = simulations.filter(_ => _.appliedAt == null && _.staleAt == null);
  // 変更内容の表示にマスタの値を使う
  const purchaseOrders = useCollectionSubscriptionInTenant(db.collection('purchaseOrders'));
  const containers = useCollectionSubscriptionInTenant(db.collection('containers'));
  const mastersById = {
    purchaseOrders: keyBy(purchaseOrders, 'id'),
    containers: keyBy(containers, 'id'),
  };
  const operatedBy = _ => omitBy(pick(firebaseUser, ['uid', 'email', 'displayName']), isUndefined);

  const onSubmitNew = async (values, { onClickClose, }) => {
    try {
      await simulationsRef.doc().set({
        ...values,
        tenantId: tenant.id,
        changes: [],
        // クエリで絞り込むため、未設定でも明示的に null を入れる
        appliedAt: null,
        staleAt: null,
        createdAt: new Date(),
        createdBy: operatedBy(),
      });
      toast.success('シミュレーションを作成しました');
      onClickClose();
    } catch (e) {
      console.error(e);
      toast.error('作成に失敗しました');
    }
  };

  const onClickDiscard = async (simulation) => {
    if (!window.confirm(`「${simulation.name}」を破棄しますか？変更内容は失われます。`)) return;
    try {
      await discardSimulation(simulation.id);
      toast.success('破棄しました');
    } catch (e) {
      console.error(e);
      toast.error('破棄に失敗しました');
    }
  };

  const onRemoveChange = async (simulation, collectionName, id) => {
    await simulation.ref.update({
      changes: removeChangeFromList(simulation.changes ?? [], collectionName, id),
      updatedAt: new Date(),
    });
    toast.success('取り消しました');
  };

  const onClickApply = async (simulation, { toggleModal }) => {
    try {
      await applySimulationToMaster(simulation, tenant.id, { operatedBy: operatedBy() });
      toast.success('マスタに適用しました');
      toggleModal(false);
    } catch (e) {
      console.error(e);
      toast.error('適用に失敗しました');
    }
  };

  return (
    <div>
      <div className="admin-simulations container-fluid py-5 position-relative bg-white">
        <InventoriesHeader activeTab="simulations" />
        <div className="mt-4 d-flex justify-content-between align-items-center mb-3">
          <div className="text-muted small">
            シミュレーション中はマスタの編集ができません。適用または破棄すると解除されます。
            <br />
            マスタに適用すると、他のシミュレーションは前提が変わるため失効します。
          </div>
          <ModalButton
            color="primary"
            label="新規シミュレーション"
            Modal={ModelFormModal}
            modalProps={{
              title: 'シミュレーション作成',
              values: {},
              fields: fields(),
              onSubmit: onSubmitNew,
              submitLabel: '作成',
              hasSpin: true,
            }}
            disabled={!canUpdateSimulation(user)}
          />
        </div>
        <div>
          {
            simulations.length > 0 ? (
              <table className="table table-bordered table-sm">
                <thead className="thead-light text-center">
                  <tr>
                    <th style={{ minWidth: 70 }}></th>
                    <th style={{ minWidth: 180 }}>名称</th>
                    <th style={{ minWidth: 200 }}>メモ</th>
                    <th style={{ minWidth: 80 }}>変更数</th>
                    <th style={{ minWidth: 130 }}>作成日時</th>
                    <th style={{ minWidth: 160 }}>状態</th>
                    <th style={{ minWidth: 210 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    simulations.map((simulation) => {
                      const { id, ref, name, note, createdAt, appliedAt, staleAt, staleReason, } = simulation;
                      const changes = simulation.changes ?? [];
                      const isStale = appliedAt != null || staleAt != null;

                      return (
                        <tr key={id}>
                          <td className="text-nowrap">
                            <EditButton
                              size="sm"
                              itemRef={ref}
                              FormModal={ModelFormModal}
                              formProps={{ title: 'シミュレーション', fields: fields(), }}
                              disabled={!canUpdateSimulation(user) || isStale}
                            />
                          </td>
                          <td>
                            <TenantLink to={`/admin/inventories/schedule?simulationId=${id}`}>
                              {name}
                            </TenantLink>
                          </td>
                          <td style={{ whiteSpace: 'pre-line' }} className="small">
                            {note}
                          </td>
                          <td className="text-center">
                            {
                              changes.length > 0 ? (
                                <ModalButton
                                  color="link"
                                  size="sm"
                                  label={`${changes.length}件`}
                                  title={`変更内容 — ${name}`}
                                  modalProps={{ size: 'lg' }}
                                  content={_ => (
                                    <SimulationChangesTable
                                      changes={changes}
                                      mastersById={mastersById}
                                      onRemove={canUpdateSimulation(user) && !isStale ? onRemoveChange.bind(null, simulation) : undefined}
                                    />
                                  )}
                                />
                              ) : <span className="text-muted">0</span>
                            }
                          </td>
                          <td className="text-center small">
                            {createdAt && formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
                          </td>
                          <td className="text-center small">
                            {
                              appliedAt ? (
                                <span className="badge badge-success">
                                  適用済み {formatDate(appliedAt.toDate(), 'yyyy/MM/dd')}
                                </span>
                              ) : staleAt ? (
                                <span className="badge badge-secondary" title={staleReason}>失効</span>
                              ) : (
                                <span className="badge badge-warning">検討中（マスタをロック）</span>
                              )
                            }
                          </td>
                          <td className="text-nowrap">
                            {
                              !isStale && (
                                <ModalButton
                                  color="warning"
                                  size="sm"
                                  className="mr-1"
                                  label="マスタに適用"
                                  title={`マスタに適用 — ${name}`}
                                  modalProps={{ size: 'lg' }}
                                  disabled={!canApplySimulation(user) || changes.length === 0}
                                  content={({ toggleModal }) => (
                                    <Fragment>
                                      <SimulationChangesTable changes={changes} mastersById={mastersById} />
                                      <div className="alert alert-warning mt-3">
                                        上記の内容でマスタを更新します。この操作は取り消せません。
                                        <div className="small mt-1">
                                          適用直前のマスタは自動でバックアップされます。
                                          {activeSimulations.length > 1 && (
                                            <Fragment>
                                              <br />
                                              他の検討中シミュレーション{activeSimulations.length - 1}件は失効します。
                                            </Fragment>
                                          )}
                                        </div>
                                      </div>
                                      <div className="d-flex justify-content-end">
                                        <ProgressButton
                                          color="warning"
                                          label="マスタに適用する"
                                          process={_ => onClickApply(simulation, { toggleModal })}
                                        />
                                      </div>
                                    </Fragment>
                                  )}
                                />
                              )
                            }
                            <ProgressButton
                              size="sm"
                              color="danger"
                              label={isStale ? '削除' : '破棄'}
                              process={onClickDiscard.bind(null, simulation)}
                              disabled={!canUpdateSimulation(user)}
                            />
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                シミュレーションは未作成です
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});
